import { Button, HStack, Text } from '@chakra-ui/react';
import { X as XIcon } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CalculatePriceResponse } from 'api/responseTypes';
import { calculateDiscountPercentage } from '../utils/calculateDiscountPercentage';

type AppliedPromoCodeProps = {
  promoCode: string;
  priceCalculation?: CalculatePriceResponse | null;
  onRemove: () => void;
  isRemoving?: boolean;
};

/**
 * The applied-code row (TZ-CO-8): the code, its "% off", and a plain "ازالة" action — replacing the
 * bordered green card with a badge, the code as a headline and a bare ✕ icon.
 *
 * The "% off" stays because nothing else conveys it: the discount line gives the absolute amount,
 * while the percentage is what tells the buyer whether the code was worth using.
 *
 * Removal behaviour is untouched — same `onRemove`, same `aria-label`, same loading/disabled gating,
 * which is what lets the TZ-CO-8 behaviour lock keep addressing this control.
 */
export const AppliedPromoCode = ({
  promoCode,
  priceCalculation,
  onRemove,
  isRemoving = false,
}: AppliedPromoCodeProps) => {
  const { formatMessage } = useIntl();
  const discountPercentage = calculateDiscountPercentage(priceCalculation);

  return (
    <HStack justifyContent='space-between' alignItems='center' w='100%' dir='rtl' spacing='8px'>
      {/* No success wording here: `PromoCodeStatus` renders it with the tick, and says whether the
          code covered *all* tickets or only some — a distinction a generic "applied successfully"
          line would flatten. This row is the code, its value, and the way out. */}
      <HStack spacing='6px' minW='0'>
        <Text variant='bodySmallRegular' color='dark.D400' dir='ltr' noOfLines={1}>
          {promoCode}
        </Text>
        {discountPercentage !== null && (
          // "خصم ‎25%" rather than a bare "25%": the number alone does not say what it is a
          // percentage *of*. Localised, unlike the previous hardcoded English "off".
          <Text variant='bodySmallRegular' color='dark.D400' noOfLines={1}>
            <FormattedMessage
              id='checkout.promoCode.percentOff'
              defaultMessage='{percent}% off'
              values={{ percent: discountPercentage }}
            />
          </Text>
        )}
      </HStack>
      {/* The design system's `danger` variant, not a bare text link. As a link it measured 32×18 —
          under the 44×44 minimum for a touch target — with no border, background or underline, so
          the only signal that it was interactive was its colour, and coral is also just the total's
          colour here. The outline and the ✕ make it read as an action, and the hover fill makes the
          destructive intent explicit before the tap. */}
      <Button
        type='button'
        variant='danger'
        onClick={onRemove}
        isDisabled={isRemoving}
        isLoading={isRemoving}
        flexShrink={0}
        leftIcon={<XIcon size={16} strokeWidth={2.5} aria-hidden />}
        aria-label={formatMessage({
          id: 'checkout.promoCode.remove',
          defaultMessage: 'Remove promo code',
        })}
      >
        <FormattedMessage id='checkout.promoCode.removeAction' defaultMessage='Remove' />
      </Button>
    </HStack>
  );
};
